import { GridRowModesModel, GridRenderCellParams } from "@mui/x-data-grid-pro";
import Button from "@mui/material/Button";
import { CubeDefinitionRowItem } from "types/api/SystemTenant/AROCube/CubeBuilderTypes";
import { ModalCubeDefinitionParamsProps } from "./ModalCubeDefinitionParameter";
import IconButton from "@mui/material/IconButton";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Stack, Tooltip, Typography } from "@mui/material";
import { RULES_CONSTANT } from "constant/RuleConstant";

interface CubeDefinitionParamActionButtonProps
  extends GridRenderCellParams<CubeDefinitionRowItem> {
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void;
  fieldName: "ruleTypeParams" | "consolidateRuleParams" | null;
  setModalParameterState: (
    newState: (
      OldState: ModalCubeDefinitionParamsProps
    ) => ModalCubeDefinitionParamsProps
  ) => void;
  disabled?: boolean;
}

export function CubeDefinitionParamActionButton(
  props: CubeDefinitionParamActionButtonProps
) {
  const {
    hasFocus,
    row,
    setModalParameterState,
    fieldName,
    disabled = false,
  } = props;
  const isConsolidate = fieldName === "consolidateRuleParams";

  if (!fieldName || row?.isNew) return <span></span>;

  const handleOnClick = () => {
    if (fieldName) {
      setModalParameterState((prevState) => ({
        ...prevState,
        visible: true,
        cubeDefinitionItem: row,
        fieldName,
      }));
    }
  };

  if (!isConsolidate) {
    if (
      !row.ruleType?.isRequiredParams ||
      row.ruleType?.isRequiredParams === "N"
    )
      return <span></span>;
  }

  if (
    !isConsolidate &&
    row.ruleType?.code ===
      RULES_CONSTANT.CUBE.RULE_CUBE_MAP_DATA_FROM_CUBE_TO_CUBE
  ) {
    let matchesFields = false;
    let usesDefinition = false;
    let conditionCount = 0;
    try {
      const config = JSON.parse(row.ruleTypeParams || "{}");
      matchesFields = config?.memberMatching === "FIELDS";
      usesDefinition = config?.memberMatching === "DEFINITION";
      conditionCount = Array.isArray(config?.fieldMatches)
        ? config.fieldMatches.length
        : 0;
    } catch {
      // Legacy mappings can have plain-text parameters.
    }
    const configured = Boolean(row.targetPullCube?.value);
    const label = !configured
      ? "Configure"
      : usesDefinition
      ? "Definition · Sum"
      : matchesFields
      ? "Fields · Sum"
      : "Same name";
    const sourceDescription = configured
      ? `${row.targetPullCube?.label} → ${
          row.targetPullCubeAttribute?.label || row.attribute.label
        }`
      : "Choose the source cube, measure, and matching conditions";
    return (
      <Tooltip title={sourceDescription}>
        <span>
          <Button
            size="small"
            startIcon={<ModeEditOutlineOutlinedIcon fontSize="small" />}
            aria-label={`${configured ? "Edit" : "Configure"} mapping for ${
              row.attribute.label
            }`}
            onClick={handleOnClick}
            tabIndex={hasFocus ? 0 : -1}
            disabled={disabled}
            sx={{
              textTransform: "none",
              minWidth: 0,
              px: 0.5,
              textAlign: "left",
            }}
          >
            <Stack alignItems="flex-start">
              <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                {label}
              </Typography>
              {configured && matchesFields && (
                <Typography variant="caption" color="text.secondary">
                  {conditionCount}{" "}
                  {conditionCount === 1 ? "condition" : "conditions"}
                </Typography>
              )}
            </Stack>
          </Button>
        </span>
      </Tooltip>
    );
  }

  if (row[fieldName]) {
    return (
      <Stack direction="row" alignItems="center">
        <IconButton
          onClick={handleOnClick}
          aria-label="delete"
          size="small"
          disabled={disabled}
        >
          <ModeEditOutlineOutlinedIcon fontSize="inherit" />
        </IconButton>
        <div>{row[fieldName]}</div>
      </Stack>
    );
  }

  return (
    <Button
      variant="secondary-table-panel"
      sx={{
        height: "58%",
        fontSize: "12px",
      }}
      onClick={handleOnClick}
      tabIndex={hasFocus ? 0 : -1}
      disabled={disabled}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === " ") {
          // Prevent key navigation when focus is on button
          event.stopPropagation();
        }
      }}
    >
      {row[fieldName] ? "View" : isConsolidate ? "Add" : "Create"}
    </Button>
  );
}
